import { useMemo, useState } from "react"
import { Box, Checkbox, HStack, Input, NativeSelect, Spinner, Stack, Table } from "@chakra-ui/react"
import { Button, PaginationControls, Text } from "@/components"
import { toaster } from "@/components/ui"
import { usePagination } from "@/hooks"
import { Employee } from "@/types"
import { updateEmployeeMiscCode, updateEmployeeLocation, bulkUpdateEmployeeLocations } from "@/api/employees"

interface Props {
  isLoading: boolean
  employees: Employee[]
  selectedEmployees: Set<string>
  onSelectionChange: (selectedIds: Set<string>) => void
}

const ITEMS_PER_PAGE = 50

const MISC_CODE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "—" },
  { value: "1", label: "1 - Engineering" },
  { value: "2", label: "2 - Marketing" },
  { value: "3", label: "3 - Sales" },
  { value: "4", label: "4 - Operations" },
]

function miscCodeLabel(code: string | null): string {
  if (!code) return "—"
  const opt = MISC_CODE_OPTIONS.find((o) => o.value === code)
  return opt ? opt.label : code
}

const EXCLUDED_JOB_TITLE_KEYWORDS = [
  "intern",
  "student",
  "board",
  "investor",
  "contract",
  "freelanc",
  "onboard",
  "interna",
  "investor rel",
]

/** Segments allowed to appear twice (city name = state name) */
const ALLOWED_DUPLICATE_SEGMENTS = new Set(["new york"])

/** Detect locations with duplicate segments (e.g. "United States, United States") */
function hasBadLocation(location: string | null): boolean {
  if (!location) return false
  const parts = location.split(",").map((p) => p.trim()).filter(Boolean)
  const counts = new Map<string, number>()
  for (const part of parts) {
    const lower = part.toLowerCase()
    counts.set(lower, (counts.get(lower) || 0) + 1)
  }
  for (const [segment, count] of counts) {
    const allowed = ALLOWED_DUPLICATE_SEGMENTS.has(segment) ? 2 : 1
    if (count > allowed) return true
  }
  return false
}

/** De-duplicate location segments while preserving order */
function suggestFixedLocation(location: string): string {
  const parts = location.split(",").map((p) => p.trim()).filter(Boolean)
  const counts = new Map<string, number>()
  const result: string[] = []
  for (const part of parts) {
    const lower = part.toLowerCase()
    const current = counts.get(lower) || 0
    const allowed = ALLOWED_DUPLICATE_SEGMENTS.has(lower) ? 2 : 1
    if (current < allowed) {
      counts.set(lower, current + 1)
      result.push(part)
    }
  }
  return result.join(", ")
}

type SortKey = "name" | "jobTitle" | "location" | "startDate" | "endDate" | "miscCode" | "connections" | "experience"
type SortDir = "asc" | "desc"

export const DepartedEmployeesView = ({ isLoading, employees, selectedEmployees, onSelectionChange }: Props) => {
  const [excludeFilterActive, setExcludeFilterActive] = useState(false)
  const [badDataFilterActive, setBadDataFilterActive] = useState(false)
  const [search, setSearch] = useState("")
  const [editingMiscId, setEditingMiscId] = useState<number | null>(null)
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null)
  const [editingLocationValue, setEditingLocationValue] = useState("")
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>("asc")
  // Local overrides so the UI reflects the change immediately without refetching
  const [miscOverrides, setMiscOverrides] = useState<Map<number, string | null>>(new Map())
  const [locationOverrides, setLocationOverrides] = useState<Map<number, string | null>>(new Map())

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return " ↕"
    return sortDir === "asc" ? " ↑" : " ↓"
  }

  const handleMiscCodeChange = async (employee: Employee, newCode: string) => {
    const value = newCode === "" ? null : newCode
    setMiscOverrides((prev) => new Map(prev).set(employee.id, value))
    setEditingMiscId(null)
    try {
      await updateEmployeeMiscCode(employee.id, value)
    } catch (e) {
      console.error("Failed to update misc code:", e)
      // Revert on failure
      setMiscOverrides((prev) => {
        const next = new Map(prev)
        next.delete(employee.id)
        return next
      })
    }
  }

  const handleLocationSave = async (employee: Employee) => {
    const value = editingLocationValue.trim() || null
    setLocationOverrides((prev) => new Map(prev).set(employee.id, value))
    setEditingLocationId(null)
    try {
      await updateEmployeeLocation(employee.id, value)
    } catch (e) {
      console.error("Failed to update location:", e)
      setLocationOverrides((prev) => {
        const next = new Map(prev)
        next.delete(employee.id)
        return next
      })
    }
  }

  const getEmployeeLocation = (employee: Employee): string | null => {
    return locationOverrides.has(employee.id) ? locationOverrides.get(employee.id)! : employee.location
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    let list = employees

    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((e) =>
        e.name.toLowerCase().includes(q) || e.jobTitle.toLowerCase().includes(q)
      )
    }

    if (excludeFilterActive) {
      list = list.filter((e) => {
        const jobTitleLower = e.jobTitle.toLowerCase()
        return EXCLUDED_JOB_TITLE_KEYWORDS.some((keyword) => jobTitleLower.includes(keyword))
      })
    }

    if (badDataFilterActive) {
      list = list.filter((e) => {
        const loc = locationOverrides.has(e.id) ? locationOverrides.get(e.id)! : e.location
        return hasBadLocation(loc)
      })
    }

    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1
      list = [...list].sort((a, b) => {
        let av: string | number | null
        let bv: string | number | null
        switch (sortKey) {
          case "name": av = a.name; bv = b.name; break
          case "jobTitle": av = a.jobTitle; bv = b.jobTitle; break
          case "location":
            av = locationOverrides.has(a.id) ? locationOverrides.get(a.id)! : a.location
            bv = locationOverrides.has(b.id) ? locationOverrides.get(b.id)! : b.location
            break
          case "startDate": av = a.liStartDate; bv = b.liStartDate; break
          case "endDate": av = a.liEndDate; bv = b.liEndDate; break
          case "miscCode":
            av = miscOverrides.has(a.id) ? miscOverrides.get(a.id)! : a.miscCode
            bv = miscOverrides.has(b.id) ? miscOverrides.get(b.id)! : b.miscCode
            break
          case "connections": av = a.connections; bv = b.connections; break
          case "experience": av = a.experienceCount; bv = b.experienceCount; break
        }
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    }

    return list
  }, [employees, search, excludeFilterActive, badDataFilterActive, sortKey, sortDir, miscOverrides, locationOverrides])

  const { paginatedData: paginatedEmployees, currentPage, totalPages, startIndex, endIndex, setPage, getPageNumbers } = usePagination(filteredEmployees, ITEMS_PER_PAGE)

  const handleFilterToggle = () => {
    setExcludeFilterActive(!excludeFilterActive)
  }

  const handleSelectAll = () => {
    const allIds = new Set(paginatedEmployees.map((e) => e.id.toString()))
    const newSelection = new Set(selectedEmployees)

    const allSelected = paginatedEmployees.every((e) => selectedEmployees.has(e.id.toString()))

    if (allSelected) {
      // Deselect all on current page
      paginatedEmployees.forEach((e) => newSelection.delete(e.id.toString()))
    } else {
      // Select all on current page
      paginatedEmployees.forEach((e) => newSelection.add(e.id.toString()))
    }

    onSelectionChange(newSelection)
  }

  const handleSelectOne = (id: string) => {
    const newSelection = new Set(selectedEmployees)
    if (newSelection.has(id)) {
      newSelection.delete(id)
    } else {
      newSelection.add(id)
    }
    onSelectionChange(newSelection)
  }

  const allOnPageSelected = paginatedEmployees.length > 0 && paginatedEmployees.every((e) => selectedEmployees.has(e.id.toString()))
  const someOnPageSelected = paginatedEmployees.some((e) => selectedEmployees.has(e.id.toString()))

  // Check if job title contains any excluded keyword
  const hasExcludedKeyword = (jobTitle: string): boolean => {
    const jobTitleLower = jobTitle.toLowerCase()
    return EXCLUDED_JOB_TITLE_KEYWORDS.some((keyword) => jobTitleLower.includes(keyword))
  }

  // Highlight matching keywords in job title
  const highlightJobTitle = (jobTitle: string): React.ReactNode => {
    const jobTitleLower = jobTitle.toLowerCase()

    // Find all matching keywords and their positions
    const matches: { start: number; end: number; keyword: string }[] = []

    EXCLUDED_JOB_TITLE_KEYWORDS.forEach((keyword) => {
      let startIndex = 0
      while (true) {
        const index = jobTitleLower.indexOf(keyword, startIndex)
        if (index === -1) break
        matches.push({ start: index, end: index + keyword.length, keyword })
        startIndex = index + 1
      }
    })

    if (matches.length === 0) {
      return jobTitle
    }

    // Sort matches by start position and merge overlapping
    matches.sort((a, b) => a.start - b.start)
    const mergedMatches: { start: number; end: number }[] = []

    for (const match of matches) {
      if (mergedMatches.length === 0) {
        mergedMatches.push({ start: match.start, end: match.end })
      } else {
        const last = mergedMatches[mergedMatches.length - 1]
        if (match.start <= last.end) {
          last.end = Math.max(last.end, match.end)
        } else {
          mergedMatches.push({ start: match.start, end: match.end })
        }
      }
    }

    // Build the highlighted string
    const parts: React.ReactNode[] = []
    let lastEnd = 0

    mergedMatches.forEach((match, index) => {
      if (match.start > lastEnd) {
        parts.push(jobTitle.slice(lastEnd, match.start))
      }
      parts.push(
        <span key={index} style={{ backgroundColor: "#FED7D7", fontWeight: "bold" }}>
          {jobTitle.slice(match.start, match.end)}
        </span>
      )
      lastEnd = match.end
    })

    if (lastEnd < jobTitle.length) {
      parts.push(jobTitle.slice(lastEnd))
    }

    return <>{parts}</>
  }

  // Select all filtered employees (across all pages)
  const handleSelectAllFiltered = () => {
    const newSelection = new Set(selectedEmployees)
    const allFilteredSelected = filteredEmployees.every((e) => selectedEmployees.has(e.id.toString()))

    if (allFilteredSelected) {
      // Deselect all filtered
      filteredEmployees.forEach((e) => newSelection.delete(e.id.toString()))
    } else {
      // Select all filtered
      filteredEmployees.forEach((e) => newSelection.add(e.id.toString()))
    }

    onSelectionChange(newSelection)
  }

  const allFilteredSelected = filteredEmployees.length > 0 && filteredEmployees.every((e) => selectedEmployees.has(e.id.toString()))

  const [isFixingAll, setIsFixingAll] = useState(false)

  // Count selected employees that have bad location data
  const selectedBadLocationCount = useMemo(() => {
    if (!badDataFilterActive || selectedEmployees.size === 0) return 0
    return employees.filter((e) => {
      if (!selectedEmployees.has(e.id.toString())) return false
      const loc = locationOverrides.has(e.id) ? locationOverrides.get(e.id)! : e.location
      return hasBadLocation(loc)
    }).length
  }, [badDataFilterActive, selectedEmployees, employees, locationOverrides])

  const handleFixAllSelected = async () => {
    const toFix = employees.filter((e) => {
      if (!selectedEmployees.has(e.id.toString())) return false
      const loc = locationOverrides.has(e.id) ? locationOverrides.get(e.id)! : e.location
      return hasBadLocation(loc)
    })
    if (toFix.length === 0) return

    setIsFixingAll(true)

    // Build the fix list and apply all overrides immediately for instant UI feedback
    const newOverrides = new Map(locationOverrides)
    const fixes: { eventId: number; location: string }[] = []
    for (const emp of toFix) {
      const loc = locationOverrides.has(emp.id) ? locationOverrides.get(emp.id)! : emp.location
      const fixed = suggestFixedLocation(loc!)
      newOverrides.set(emp.id, fixed)
      fixes.push({ eventId: emp.id, location: fixed })
    }
    setLocationOverrides(newOverrides)

    // Single bulk request — backend handles batching to IT Works
    try {
      const { successCount, failedCount } = await bulkUpdateEmployeeLocations(fixes)
      if (failedCount > 0) {
        toaster.create({
          title: `Fixed ${successCount} of ${fixes.length} locations (${failedCount} failed)`,
          type: "warning",
        })
      } else {
        toaster.success({ title: `Fixed ${successCount} location${successCount !== 1 ? "s" : ""}` })
      }
    } catch {
      // Total failure — revert all overrides
      setLocationOverrides((prev) => {
        const next = new Map(prev)
        for (const { eventId } of fixes) next.delete(eventId)
        return next
      })
      toaster.create({ title: "Failed to fix locations", type: "error" })
    }

    // Clear selection and exit bad data filter
    onSelectionChange(new Set())
    setBadDataFilterActive(false)
    setIsFixingAll(false)
  }

  if (isLoading) {
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Departed Employees
        </Text>
        <Spinner size="md" color="blue.600" />
      </Box>
    )
  }

  if (employees.length === 0) {
    return (
      <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Departed Employees
        </Text>
        <Text color="text.muted">No departed employees found.</Text>
      </Box>
    )
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Departed Employees ({filteredEmployees.length} of {employees.length})
        </Text>
        <HStack gap={3}>
          <Input
            placeholder="Search by name or title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxW="250px"
            size="sm"
          />
          {selectedEmployees.size > 0 && (
            <Text fontSize="sm" color="blue.600" fontWeight="medium">
              {selectedEmployees.size} selected
            </Text>
          )}
          {(excludeFilterActive || badDataFilterActive) && (
            <Button
              size="sm"
              variant="outline"
              colorScheme={allFilteredSelected ? "red" : "green"}
              onClick={handleSelectAllFiltered}
            >
              {allFilteredSelected ? "Deselect All Filtered" : `Select All Filtered (${filteredEmployees.length})`}
            </Button>
          )}
          {badDataFilterActive && selectedBadLocationCount > 0 && (
            <Button
              size="sm"
              variant="solid"
              colorScheme="green"
              onClick={handleFixAllSelected}
              disabled={isFixingAll}
            >
              {isFixingAll ? "Fixing..." : `Fix All Selected (${selectedBadLocationCount})`}
            </Button>
          )}
          <Button
            size="sm"
            variant={excludeFilterActive ? "solid" : "outline"}
            onClick={handleFilterToggle}
          >
            {excludeFilterActive ? "Showing Filtered" : "Show Bad Job Titles Only"}
          </Button>
          <Button
            size="sm"
            variant={badDataFilterActive ? "solid" : "outline"}
            onClick={() => setBadDataFilterActive(!badDataFilterActive)}
          >
            {badDataFilterActive ? "Showing Bad Data" : "Show Bad Data"}
          </Button>
        </HStack>
      </HStack>

      <Box overflowX="auto">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="50px">
                <Checkbox.Root
                  checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                  onCheckedChange={handleSelectAll}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                </Checkbox.Root>
              </Table.ColumnHeader>
              <Table.ColumnHeader minW="180px" cursor="pointer" onClick={() => handleSort("name")}>Name{sortIndicator("name")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="200px" cursor="pointer" onClick={() => handleSort("jobTitle")}>Job Title{sortIndicator("jobTitle")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="150px" cursor="pointer" onClick={() => handleSort("location")}>Location{sortIndicator("location")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="120px" cursor="pointer" onClick={() => handleSort("startDate")}>Start Date{sortIndicator("startDate")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="120px" cursor="pointer" onClick={() => handleSort("endDate")}>End Date{sortIndicator("endDate")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="120px" cursor="pointer" onClick={() => handleSort("miscCode")}>Misc Code{sortIndicator("miscCode")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="100px" cursor="pointer" onClick={() => handleSort("connections")}>Connections{sortIndicator("connections")}</Table.ColumnHeader>
              <Table.ColumnHeader minW="100px" cursor="pointer" onClick={() => handleSort("experience")}>Experience{sortIndicator("experience")}</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedEmployees.map((employee) => {
              const isSelected = selectedEmployees.has(employee.id.toString())
              const isExcluded = hasExcludedKeyword(employee.jobTitle)

              // Determine row background color
              let rowBg: string | undefined
              if (isSelected) {
                rowBg = "row.blue"
              } else if (isExcluded) {
                rowBg = "row.red"
              }

              let hoverBg: string
              if (isSelected) {
                hoverBg = "hover.blue"
              } else if (isExcluded) {
                hoverBg = "hover.red"
              } else {
                hoverBg = "hover.bg"
              }

              return (
                <Table.Row
                  key={employee.id}
                  bg={rowBg}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleSelectOne(employee.id.toString())}
                >
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <Checkbox.Root
                      checked={isSelected}
                      onCheckedChange={() => handleSelectOne(employee.id.toString())}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.Cell>
                  <Table.Cell fontWeight="medium" whiteSpace="nowrap">{employee.name}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{highlightJobTitle(employee.jobTitle)}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap" onClick={(e) => e.stopPropagation()}>
                    {editingLocationId === employee.id ? (
                      <Input
                        size="sm"
                        maxW="180px"
                        value={editingLocationValue}
                        onChange={(e) => setEditingLocationValue(e.target.value)}
                        onBlur={() => handleLocationSave(employee)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleLocationSave(employee); if (e.key === "Escape") setEditingLocationId(null) }}
                        autoFocus
                      />
                    ) : (
                      <Stack gap={0}>
                        <Text
                          fontSize="sm"
                          cursor="pointer"
                          _hover={{ textDecoration: "underline" }}
                          onClick={() => { setEditingLocationId(employee.id); setEditingLocationValue(getEmployeeLocation(employee) || "") }}
                          color={badDataFilterActive && hasBadLocation(getEmployeeLocation(employee)) ? "red.500" : undefined}
                          textDecoration={badDataFilterActive && hasBadLocation(getEmployeeLocation(employee)) ? "line-through" : undefined}
                        >
                          {getEmployeeLocation(employee) || "N/A"}
                        </Text>
                        {badDataFilterActive && hasBadLocation(getEmployeeLocation(employee)) && (
                          <Text
                            fontSize="xs"
                            color="green.600"
                            cursor="pointer"
                            fontWeight="medium"
                            _hover={{ textDecoration: "underline" }}
                            onClick={async () => {
                              const fixed = suggestFixedLocation(getEmployeeLocation(employee)!)
                              setLocationOverrides((prev) => new Map(prev).set(employee.id, fixed))
                              try {
                                await updateEmployeeLocation(employee.id, fixed)
                              } catch {
                                setLocationOverrides((prev) => { const next = new Map(prev); next.delete(employee.id); return next })
                              }
                            }}
                          >
                            Fix: {suggestFixedLocation(getEmployeeLocation(employee)!)}
                          </Text>
                        )}
                      </Stack>
                    )}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{employee.liStartDateFormatted || formatDate(employee.liStartDate)}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{employee.liEndDateFormatted || formatDate(employee.liEndDate || "")}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap" onClick={(e) => e.stopPropagation()}>
                    {editingMiscId === employee.id ? (
                      <NativeSelect.Root size="sm" maxW="150px">
                        <NativeSelect.Field
                          value={miscOverrides.has(employee.id) ? (miscOverrides.get(employee.id) ?? "") : (employee.miscCode ?? "")}
                          onChange={(e) => handleMiscCodeChange(employee, e.target.value)}
                          onBlur={() => setEditingMiscId(null)}
                          autoFocus
                        >
                          {MISC_CODE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    ) : (
                      <Text
                        fontSize="sm"
                        cursor="pointer"
                        _hover={{ textDecoration: "underline" }}
                        onClick={() => setEditingMiscId(employee.id)}
                      >
                        {miscCodeLabel(miscOverrides.has(employee.id) ? miscOverrides.get(employee.id)! : employee.miscCode)}
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{employee.connections || "N/A"}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap">{employee.experienceCount}</Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredEmployees.length}
        startIndex={startIndex}
        endIndex={endIndex}
        onPageChange={setPage}
        getPageNumbers={getPageNumbers}
      />
    </Box>
  )
}
